'use client'

import { Image } from '@macarongfactory/web-ui'
import Link from 'next/link'
import { useRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import { ROUTES } from '@/shared/lib/routes'
import { OptimizedImage } from '@/shared/ui'

import { SWIPER_HORIZONTAL_PADDING_STYLE } from '../lib'
import { carStoryTracking } from '../lib/tracking'
import { type IMycleContentMain, MYCLE_CONTENT_MENU } from '../model'
import useTrackOnVisible from '../model/useTrackOnVisible'

interface IDriveMagazineSwiperProps {
  items: IMycleContentMain[]
}

const DriveMagazineSwiper = ({ items }: IDriveMagazineSwiperProps) => {
  const hasScrolledRef = useRef(false)

  if (items.length === 0) return null

  const handleSlideChange = () => {
    if (hasScrolledRef.current) return
    hasScrolledRef.current = true
    carStoryTracking.scrollTopContents()
  }

  return (
    <section className='py-dimension-x5 pb-dimension-x6'>
      <Swiper
        slidesPerView='auto'
        spaceBetween={12}
        initialSlide={0}
        onSlideChange={handleSlideChange}
        style={SWIPER_HORIZONTAL_PADDING_STYLE}
      >
        {items.map((item, index) => (
          <SwiperSlide key={item.serverId} className='!w-[264px] md:!w-[400px]'>
            <DriveMagazineCard item={item} index={index} />
          </SwiperSlide>
        ))}
      </Swiper>
    </section>
  )
}

const DriveMagazineCard = ({
  item,
  index,
}: {
  item: IMycleContentMain
  index: number
}) => {
  const cardRef = useRef<HTMLAnchorElement | null>(null)
  useTrackOnVisible({
    ref: cardRef,
    tracker: () => carStoryTracking.showTopContentCard(item.title, index),
  })

  const handleClick = () =>
    carStoryTracking.clickTopContentCard(item.title, index)
  const className = 'rounded-dimension-x5 relative block overflow-hidden'

  const cardContent = (
    <>
      <div className='bg-background-neutral-weak relative aspect-5/4 w-full overflow-hidden'>
        <OptimizedImage
          src={item.thumbnailUrl}
          alt={item.title}
          width='100%'
          height='100%'
          radius={0}
          scaletype='crop'
          sizes='(min-width: 768px) 400px, 264px'
          fallback={<Image.Fallback />}
        />
        <span className='bg-background-overlay-strong text-body-1-medium text-foreground-static-white top-dimension-x5 left-dimension-x5 px-dimension-x2-5 rounded-full py-dimension-x1 absolute'>
          {item.main.label}
        </span>
      </div>
      <div
        className='px-dimension-x5 py-dimension-x4 flex h-[83px] items-center'
        style={{ backgroundColor: item.main.bgColor }}
      >
        <p
          className='text-title-1-semibold line-clamp-2 whitespace-pre-line'
          style={{ color: item.main.textColor }}
        >
          {item.title}
        </p>
      </div>
    </>
  )

  // NOW 메뉴는 어드민에서 발행된 외부 이벤트 페이지(contentUrl)로 직접 이동
  if (item.menu === MYCLE_CONTENT_MENU.NOW && item.contentUrl) {
    return (
      <a
        ref={cardRef}
        href={item.contentUrl}
        onClick={handleClick}
        className={className}
      >
        {cardContent}
      </a>
    )
  }

  return (
    <Link
      ref={cardRef}
      href={ROUTES.CAR_STORY.CONTENT_DETAIL(item.serverId)}
      onClick={handleClick}
      className={className}
    >
      {cardContent}
    </Link>
  )
}

export default DriveMagazineSwiper
